import React, { useState, useEffect } from 'react';
import { Grid, Tooltip, useMediaQuery, useTheme } from '@mui/material';

import Button from '@mui/material/Button';
import { useNavigate } from 'react-router-dom';

import ozETHLOGO from '../../Assets/images/Icons/ozETH.svg';

import OZUSDLOGO from '../../Assets/images/Icons/ozUSD.svg';

import { ReactComponent as CircleInfoSvg } from '../../Assets/images/circle-info.svg';
import { useAirdrop } from './useAirdrop';
import { isDisabled } from '../../utils/constants';
import Web3 from 'web3';
import { useAccount } from 'wagmi';
import {
  REACT_APP_OPENZK_RPC,
  REACT_APP_OZUSD_L2_TOKEN,
} from '../../Config/Config';
import OZKToken from '../../ContractABI/OPENZKToken.json';
import { AirdropWrapper } from './AirdropWrapper';

export const AirdropStats = () => {
  const {
    ethMultiplier,
    stablecoinMultiplier,
    airdropPoints,
    isAirdropPaused,
  } = useAirdrop();

  const [ethAmountBridged, setEthAmountBridged] = useState(BigInt(0));
  const [ozUSDAmountBridged, setOzUSDAmountBridged] = useState(BigInt(0));
  const [openOZETH, setOpenOZETH] = useState(false);
  const [openOZUSD, setOpenOZUSD] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { address } = useAccount();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const walletProvider = new Web3(REACT_APP_OPENZK_RPC);
        const ozUSDContract = new walletProvider.eth.Contract(
          OZKToken,
          REACT_APP_OZUSD_L2_TOKEN,
        );
        // Fetch both APIs in parallel
        const [ozUSDResponse, ethResponse] = await Promise.all([
          ozUSDContract.methods
            .balanceOf(address)
            .call()
            .then((res) => (typeof res === 'bigint' ? res : BigInt(0))),
          walletProvider.eth.getBalance(address).then((res) => res),
        ]);

        setEthAmountBridged(ethResponse / BigInt(10 ** 14)); // Adjust according to API response
        setOzUSDAmountBridged(ozUSDResponse / BigInt(10 ** 14));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [ethAmountBridged, ozUSDAmountBridged, address]);

  const navigate = useNavigate();

  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  });

  const pointsformatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });

  // const fx = [
  //   { id: 1, icon: USDTLOGO, title: 'USDT : ozUSD', value: '' },
  //   { id: 2, icon: USDCLOGO, title: 'USDC : ozUSD', value: '' },
  //   { id: 3, icon: DAILOGO, title: 'DAI : ozUSD', value: '' },
  //   { id: 4, icon: ETHLOGO, title: 'ETH : ozETH', value: '' },
  // ];

  return (
    <Grid
      container
      display={'flex'}
      gap={{ md: '32px', sm: '23px' }}
      flexDirection={'column'}
      alignItems={{ xs: 'center', md: 'stretch' }}
      justifyContent={{ xs: 'center', sm: 'space-between' }}
    >
      <Grid
        container
        display={'flex'}
        gap={{ xs: '20px', sm: '0' }}
        flexDirection={'row'}
        alignItems={'stretch'}
        justifyContent={{ xs: 'center', sm: 'space-between' }}
      >
        <Grid
          sm={5.9}
          xs={11}
          className="p-3 lgcontainer"
          marginBottom={{ sm: 3, xs: 3, lg: 0, md: 0 }}
        >
          <Grid container>
            <Grid
              item
              xs={12}
              display={'flex'}
              flexDirection={'row'}
              justifyContent={'space-between'}
            >
              <p className="airdrop_headtext">Bridged and Staked</p>
              <p className="airdrop_headtext">APY</p>
            </Grid>
            <Grid
              item
              className="mt-3"
              xs={12}
              display={'flex'}
              flexDirection={'row'}
              alignItems={'center'}
              justifyContent={'space-between'}
            >
              <Grid item display={'flex'} alignItems={'center'}>
                <p className="airdrop_text">
                  {formatter.format(Number(ethAmountBridged) / 10000)}
                </p>
                <img
                  src={ozETHLOGO}
                  alt="ozETH"
                  width={'20px'}
                  height={'20px'}
                  className="mx-1"
                />
              </Grid>

              <div className="BridgeInputText d-flex gap-1">
                <p className="BridgeInputText">2.5%</p>
                <Tooltip
                  onClose={() => setOpenOZETH(false)}
                  open={openOZETH}
                  title="Calculated using historical 7 day average"
                  placement="top"
                  className="cursor-pointer"
                >
                  <CircleInfoSvg
                    width="12"
                    onClick={() => setOpenOZETH(true)}
                  />
                </Tooltip>
              </div>
            </Grid>
            <Grid
              item
              className="mt-3"
              xs={12}
              display={'flex'}
              flexDirection={'row'}
              alignItems={'center'}
              justifyContent={'space-between'}
            >
              <Grid item display={'flex'} alignItems={'center'}>
                <p className="airdrop_text">
                  {formatter.format(Number(ozUSDAmountBridged) / 10000)}
                </p>
                <img
                  src={OZUSDLOGO}
                  alt="ozUSD"
                  width={'20px'}
                  height={'20px'}
                  className="mx-1"
                />
              </Grid>

              <div className="BridgeInputText d-flex gap-1">
                <p className="BridgeInputText">4.5%</p>
                <Tooltip
                  onClose={() => setOpenOZUSD(false)}
                  open={openOZUSD}
                  title="Estimated compounded rate per annum based on SSR - does not represent or guarantee future results"
                  placement="top"
                  className="cursor-pointer"
                >
                  <CircleInfoSvg
                    width="12"
                    onClick={() => setOpenOZUSD(true)}
                  />
                </Tooltip>
              </div>
            </Grid>
            <Grid
              item
              className="mt-4 w-100"
              xs={12}
              display={'flex'}
              flexDirection={'row'}
              alignItems={'center'}
              justifyContent={'center'}
            >
              <Button
                className="mr-2"
                sx={{
                  height: '40px',
                  width: 'auto',
                  backgroundColor: '#000000',
                  border: 0.75,
                  borderColor: '#FC9D4B',
                  borderRadius: '10px',
                  color: '#FFE584',
                  fontWeight: 'semibold',
                  fontFamily: 'PoppinsRegular',
                  fontSize: {
                    xs: '10px', // For extra small screens (mobile)
                    sm: '16px', // For small screens (tablets)
                    md: '18px', // For medium screens
                    lg: '18px', // For large screens
                  },
                  padding: {
                    xs: '15px', // For extra small screens
                    sm: '12px', // For small screens
                    md: '15px', // For medium screens
                  },
                  '&:hover': {
                    background:
                      'linear-gradient(90deg, rgba(241, 215, 75, 0.8) 0%, rgba(221, 113, 42, 0.8) 65.9%)',
                    color: '#ffffff',
                  },
                  textTransform: 'none',
                }}
                onClick={() => {
                  !isDisabled && navigate('/bridge');
                }}
              >
                Bridge More
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          sm={5.9}
          xs={11}
          display={'flex'}
          flexWrap={'wrap'}
          alignItems={'flex-start'}
          justifyContent={'space-between'}
          className="p-3 lgcontainer relative"
          marginBottom={{ sm: 3, xs: 3, lg: 0, md: 0 }}
        >
          <Grid
            item
            lg={12}
            md={12}
            sm={12}
            xs={12}
            display={'flex'}
            flexDirection={'row'}
            alignItems={'flex-start'}
            justifyContent={'space-between'}
          >
            <Grid
              item
              lg={6}
              md={6}
              sm={6}
              xs={6}
              display={'flex'}
              flexDirection={'column'}
              alignItems={'center'}
              justifyContent={'flex-start'}
            >
              <p className="airdrop_headtext"> Airdrop Points </p>
              <p className="airdrop_valuetext mt-3">
                {pointsformatter.format(airdropPoints)}
              </p>
            </Grid>
            <div
              className="mx-3"
              style={{ borderRight: '1px solid #585144', height: '75px' }}
            />
            <Grid
              item
              lg={5.8}
              md={5.8}
              sm={5.8}
              xs={5.8}
              display={'flex'}
              flexDirection={'row'}
              alignItems={'center'}
              justifyContent={'space-between'}
            >
              <Grid
                item
                lg={5.8}
                md={5.8}
                sm={5.8}
                xs={5.8}
                display={'flex'}
                flexDirection={'column'}
                alignItems={'center'}
                justifyContent={'space-between'}
              >
                <p className="airdrop_headtext">
                  ETH <br />
                  Multiplier
                </p>
                <p className="airdrop_valuetext"> {ethMultiplier}X </p>
              </Grid>
              <Grid
                item
                lg={5.8}
                md={5.8}
                sm={5.8}
                xs={5.8}
                display={'flex'}
                flexDirection={'column'}
                alignItems={'center'}
                justifyContent={'space-between'}
              >
                <p className="airdrop_headtext">Stablecoin Multiplier </p>
                <p className="airdrop_valuetext"> {stablecoinMultiplier}X </p>
              </Grid>
            </Grid>
          </Grid>

          <Grid
            item
            className="w-100 mt-3"
            lg={12}
            md={12}
            sm={12}
            xs={12}
            display={'flex'}
            flexDirection={'row'}
            alignItems={'center'}
            justifyContent={'space-between'}
          >
            <Grid
              item
              className="BridgeTextBg p-2"
              lg={12}
              md={12}
              sm={12}
              xs={12}
              display={'flex'}
              flexDirection={'column'}
              alignItems={'center'}
              justifyContent={'center'}
            >
              <p className=" m-0 p-0" style={{ textAlign: 'center' }}>
                Multiplier increases
                <span style={{ color: '#E5772E' }}> 0.5x </span> for every week
                your ETH/stablecoin is bridged and staked
              </p>
            </Grid>
          </Grid>

          {isAirdropPaused && (
            <Grid className="airdropWrapper">
              <AirdropWrapper hideText={isXs} />
            </Grid>
          )}
        </Grid>
      </Grid>

      {/* <Grid
        xs={11}
        sm={24}
        md={24}
        display={'flex'}
        flexWrap={'wrap'}
        alignItems={'flex-start'}
        justifyContent={'space-between'}
        className="w-100 lgcontainer relative"
      >
        {fx.map((item) => {
          return (
            <Grid
              item
              lg={3}
              md={3}
              sm={3}
              xs={6}
              display={'flex'}
              flexDirection={'row'}
              alignItems={'center'}
              justifyContent={'end'}
              key={item.id}
            >
              <Grid
                item
                lg={12}
                md={12}
                sm={12}
                xs={12}
                display={'flex'}
                flexDirection={'column'}
                alignItems={'center'}
                justifyContent={'space-between'}
                className="m-auto py-3"
                // gap-2
              >
                <div className="flex justify-center items-center gap-2.5 w-full">
                  <img
                    src={item.icon}
                    alt={item.title}
                    width={'20px'}
                    height={'27px'}
                  />
                  <p className="airdropRateTitle"> {item.title} </p>
                </div>
                <p className="airdropRateValue">{item.value}</p>
              </Grid>
              <div
                className={cx([
                  (item.id === 1 ||
                    item.id === 3 ||
                    (item.id === 2 && !isXs)) &&
                    'verticalLine',
                ])}
              />
            </Grid>
          );
        })}
        {isXs && (
          <div className="horizontalLine absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 " />
        )}
      </Grid> */}
    </Grid>
  );
};
